import { useCallback, useMemo, useState } from "react";
import { useGetUsersQuery } from "@/services/users/usersApi";

const NO_USERS = [];

/**
 * The people an invoice can be assigned to, fetched only when something needs
 * them.
 *
 * Most visits to a queue never open an assignee menu, and the users endpoint may
 * not be open to every role, so loading it up front costs a request (and
 * possibly a 403) on every page view for nothing. Instead the list stays
 * skipped until `request()` is called — by an assignee control on hover, focus
 * or open — and stays loaded from then on. `eager` covers the cases that need
 * names straight away, such as a filter chip that has to resolve an id.
 *
 * RTK Query already dedupes identical subscriptions and caches the result (see
 * `getUsers.keepUnusedDataFor`), so every caller of this hook shares one request.
 */
export function useAssignees({ eager = false } = {}) {
  const [requested, setRequested] = useState(false);
  const { data, isError, refetch } = useGetUsersQuery(undefined, { skip: !eager && !requested });
  // A failed load is retried the next time a control asks, instead of leaving the
  // cached error in place for the rest of the page's life.
  const request = useCallback(() => {
    if (requested && isError) refetch();
    else setRequested(true);
  }, [requested, isError, refetch]);

  // Memoised so consumers' own memos are not invalidated on every render.
  const users = useMemo(() => (Array.isArray(data) ? data.filter(Boolean) : NO_USERS), [data]);

  return { users, request, isError };
}
